import { Injectable, Injector } from "@angular/core";
import { ActivatedRouteSnapshot, UrlTree, Router, Params } from "@angular/router";
import { BehaviorSubject, Observable, catchError, map, of } from "rxjs";
import { LocalStorageService } from "../local-storage/local-storage.service";
import { AdminService } from "../admin/admin.service";
import { RouteTrackingService } from "../route-tracking/route-tracking.service";
import { UserService } from "../user/user.service";
import { DialogService } from "../dialog/dialog.service";
import { CartItemService } from "../cart-item/cart-item.service";
import { WishlistService } from "../wishlistService/wishlist.service";

@Injectable({
    providedIn: "root",
})
export class AuthService {
    public isLoggedIn = new BehaviorSubject<boolean>(false);
    public isLoggedIn$ = this.isLoggedIn.asObservable();
    public permissionData: string[] = [];
    private _cartItemService!: CartItemService;
    private _wishlistService!: WishlistService;

    constructor(
        private router: Router,
        private localStorageService: LocalStorageService,
        private adminService: AdminService,
        private routeTrackingService: RouteTrackingService,
        private userService: UserService,
        private dialogService: DialogService,
        private injector: Injector,
    ) {}

    private get cartItemService(): CartItemService {
        if (!this._cartItemService) {
            this._cartItemService = this.injector.get(CartItemService);
        }
        return this._cartItemService;
    }

    private get wishlistService(): WishlistService {
        if (!this._wishlistService) {
            this._wishlistService = this.injector.get(WishlistService);
        }
        return this._wishlistService;
    }

    public hasPermission(permission: string[]): Observable<boolean> {
        return this.adminService.setUserPermissions().pipe(
            map((permissions: string[]) => permission.some((permission) => permissions.includes(permission))),
            catchError((error) => {
                return of(false); // or handle error accordingly
            }),
        );
    }

    public isAdminLoggedIn(): boolean {
        return !!this.adminToken;
    }

    public checkLoginStatus(): void {
        const token = this.getToken();
        this.isLoggedIn.next(!!token);
    }

    public get isUserLoggedIn(): boolean {
        return !!this.getToken();
    }

    private getToken(): string | null {
        return localStorage.getItem("token");
    }

    private get adminToken(): string | null {
        return localStorage.getItem("adminToken");
    }

    public removeUserToken(): void {
        localStorage.removeItem("token");
    }

    public removeAdminToken(): void {
        localStorage.removeItem("adminToken");
    }

    public logout(isAdmin: boolean = false): void {
        if (isAdmin) {
            localStorage.removeItem("adminToken");
            this.router.navigate(["/admin/login"]);
        } else {
            localStorage.removeItem("token");
            this.localStorageService.removeAllCartItems();
            this.cartItemService.resetCartItems();
            this.wishlistService.resetWishlist();
            sessionStorage.clear();
            this.isLoggedIn.next(false);
            this.router.navigate(["/home"]);
        }
    }

    public handleAdminAccess(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
        if (!this.isAdminLoggedIn()) {
            return of(this.router.parseUrl("/admin/login"));
        }

        const requiredPermission = route.data["permission"];
        if (requiredPermission) {
            return this.hasPermission(requiredPermission).pipe(
                map((hasPermission) => {
                    if (!hasPermission) {
                        return this.router.parseUrl("/admin/noAccess");
                    }
                    return true;
                }),
            );
        }

        return of(true);
    }

    public handleUserAccess(targetUrl: string, queryParams: Params): Observable<boolean | UrlTree> {
        if (!!queryParams["fromProductDetail"]) {
            return of(true);
        }
        // If not logged in, always redirect to login
        if (!this.isUserLoggedIn) {
            this.routeTrackingService.setIntendedUrl(targetUrl);
            // return of(this.router.parseUrl("/login"));
            this.dialogService.loginDialog(false, targetUrl);
            return of(false);
        }

        // Special handling for user details page
        if (targetUrl === "/userDetails") {
            return this.userService.isUserDetailsExist().pipe(
                map((exists) => {
                    // If details exist, redirect to profile
                    if (exists) {
                        return this.router.parseUrl("/account/overview");
                    }

                    return true;
                }),
                catchError(() => {
                    return of(true);
                }),
            );
        }
        return of(true);
    }
}
